import { createRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@arabquiz/ui'
import {
  Clock,
  FilePlus2,
  FileQuestion,
  Info,
  ListChecks,
  Loader2,
  Play,
  Plus,
  Shuffle,
  Sparkles,
} from 'lucide-react'
import { Route as appRoute } from '../_app'
import { RandomQuizPickerDialog } from '../../components/game/RandomQuizPickerDialog'

/**
 * Host dashboard — the landing surface a signed-in host hits first.
 *
 * Loads the host's quizzes via `GET /api/v1/quizzes` (same key as the
 * `/quizzes/` index page so TanStack Query reuses the cache across both
 * surfaces) and exposes a primary "host this quiz" action on every card.
 * The action fires `POST /api/v1/games` with the quiz id; on success we
 * navigate to `/games/host/{gameId}` for the live lobby.
 *
 * Visual direction: editorial confidence, projector-ready. Bold display
 * headline, an oversized brand-stripe rule under the hero, and quiz cards
 * with a prominent gradient Host CTA inside each card (instead of the
 * "click anywhere to edit" affordance of `/quizzes/`). The dashboard is
 * intentionally action-first — hosting trumps editing on this surface.
 *
 * Like other routes added in the same wave, the navigation target uses
 * `window.location.assign(...)` rather than `useNavigate` so this file
 * compiles before TanStack Router regenerates `routeTree.gen.ts` with
 * the new `/games/host/$gameId` entry (matches T22 / T25 / T26).
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: DashboardPage,
})

type QuizSummary = {
  id: string
  title: string
  description: string
  questionCount: number
  updatedAt: string
}

type CreateGameResponse = {
  id: string
  pin: string
}

function DashboardPage() {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  // Track which quiz's host button is currently in-flight so we can show
  // a per-card spinner and disable the row without freezing the whole
  // dashboard during the POST round-trip.
  const [pendingQuizId, setPendingQuizId] = useState<string | null>(null)
  const [randomPickerOpen, setRandomPickerOpen] = useState(false)

  const quizzesQuery = useQuery({
    queryKey: ['quizzes'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/quizzes', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load quizzes: ${res.status}`)
      }
      return (await res.json()) as QuizSummary[]
    },
  })

  const createGameMutation = useMutation({
    mutationFn: async (quizId: string) => {
      const res = await fetch('/api/v1/games', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quizId }),
      })
      if (!res.ok) {
        throw new Error(`Failed to create game: ${res.status}`)
      }
      return (await res.json()) as CreateGameResponse
    },
    onSuccess: (game) => {
      // Full-page navigation: the host route is added in the same task
      // and TanStack Router's generated tree may not include it at this
      // file's typecheck time. The next load resolves the freshly
      // regenerated tree (matches the create-quiz flow's pattern).
      window.location.assign(`/games/host/${encodeURIComponent(game.id)}`)
    },
    onError: () => {
      // Reset the inline-spinner state so the host can retry; the
      // mutation's `isError` carries the visible affordance.
      setPendingQuizId(null)
    },
  })

  const handleHost = (quizId: string) => {
    setPendingQuizId(quizId)
    createGameMutation.mutate(quizId)
  }

  return (
    <main className="min-h-screen bg-gradient-to-bl from-brand-50/40 via-white to-white">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-900 tracking-tight">
                {t('dashboardTitle')}
              </h1>
              <p className="mt-3 text-brand-700 text-base sm:text-lg max-w-xl leading-relaxed">
                {t('dashboardSubtitle')}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <a href="/quizzes" className="inline-block">
                <Button variant="outline" size="lg" className="gap-2">
                  <ListChecks className="h-5 w-5" aria-hidden="true" />
                  <span>{t('viewAllQuizzes')}</span>
                </Button>
              </a>
              <Button
                variant="outline"
                size="lg"
                className="gap-2"
                onClick={() => setRandomPickerOpen(true)}
              >
                <Shuffle className="h-5 w-5" aria-hidden="true" />
                <span>{t('startRandomQuiz')}</span>
              </Button>
              <a href="/quizzes/new" className="inline-block">
                <Button size="lg" className="gap-2">
                  <Plus className="h-5 w-5" aria-hidden="true" />
                  <span>{t('createQuiz')}</span>
                </Button>
              </a>
            </div>
          </div>
        </header>

        {quizzesQuery.isLoading ? <DashboardSkeleton /> : null}

        {quizzesQuery.isError ? (
          <div
            className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            role="alert"
          >
            {t('loadError')}
          </div>
        ) : null}

        {createGameMutation.isError ? (
          <div
            className="mb-6 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
            role="alert"
          >
            {t('hostError')}
          </div>
        ) : null}

        {quizzesQuery.isSuccess && quizzesQuery.data.length === 0 ? (
          <DashboardEmptyState
            title={t('dashboardEmptyTitle')}
            description={t('dashboardEmptyDescription')}
            ctaLabel={t('createQuiz')}
          />
        ) : null}

        {quizzesQuery.isSuccess && quizzesQuery.data.length > 0 ? (
          <ul
            role="list"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
          >
            {quizzesQuery.data.map((quiz) => (
              <li key={quiz.id}>
                <HostableQuizCard
                  quiz={quiz}
                  onHost={() => handleHost(quiz.id)}
                  isPending={pendingQuizId === quiz.id && createGameMutation.isPending}
                  hostLabel={t('hostGame')}
                  hostAriaLabel={t('hostGameAria')}
                  preparingLabel={t('preparingGame')}
                  needsQuestionsLabel={t('needsQuestions')}
                  lastUpdatedLabel={t('lastUpdated')}
                  questionCountLabel={t('questionsCount', {
                    count: quiz.questionCount,
                  })}
                />
              </li>
            ))}
          </ul>
        ) : null}

        {quizzesQuery.isLoading ? (
          <span className="sr-only" role="status" aria-live="polite">
            {tCommon('loading')}
          </span>
        ) : null}
      </div>

      <RandomQuizPickerDialog
        open={randomPickerOpen}
        onClose={() => setRandomPickerOpen(false)}
      />
    </main>
  )
}

type HostableQuizCardProps = {
  quiz: QuizSummary
  onHost: () => void
  isPending: boolean
  hostLabel: string
  hostAriaLabel: string
  preparingLabel: string
  needsQuestionsLabel: string
  lastUpdatedLabel: string
  questionCountLabel: string
}

/**
 * Quiz card variant whose primary affordance is "host a game", not "edit".
 * The title links to the editor (subordinate action) while the Host
 * button takes the visual weight — a wide, gradient CTA that lives at
 * the foot of the card so the host can scan a column of quizzes and
 * launch one without changing pointer trajectory.
 */
function HostableQuizCard({
  quiz,
  onHost,
  isPending,
  hostLabel,
  hostAriaLabel,
  preparingLabel,
  needsQuestionsLabel,
  lastUpdatedLabel,
  questionCountLabel,
}: HostableQuizCardProps) {
  const updatedAtDate = new Date(quiz.updatedAt)
  const isEmpty = quiz.questionCount === 0
  return (
    <article className="group relative flex h-full flex-col rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-5 pe-5 py-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:border-brand-200 hover:border-s-brand-600">
      <a
        href={`/quizzes/${encodeURIComponent(quiz.id)}/edit`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-md"
        aria-label={quiz.title}
      >
        <h2 className="text-lg font-bold font-display text-brand-900 tracking-tight line-clamp-2 transition-colors group-hover:text-brand-700">
          {quiz.title}
        </h2>
        {quiz.description ? (
          <p className="mt-2 text-sm text-brand-700 line-clamp-2 leading-relaxed">
            {quiz.description}
          </p>
        ) : null}
      </a>

      <div className="mt-4 flex items-center justify-between gap-2 text-xs text-brand-600">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 font-semibold text-brand-700">
          <FileQuestion className="h-3.5 w-3.5" aria-hidden="true" />
          {questionCountLabel}
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          title={updatedAtDate.toLocaleString('ar')}
        >
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">{lastUpdatedLabel}: </span>
          <time dateTime={quiz.updatedAt}>{formatRelativeAr(updatedAtDate)}</time>
        </span>
      </div>

      {/* Push the CTA to the foot of the card so a column of cards keeps
          its Host buttons aligned regardless of description length. */}
      <div className="mt-auto pt-5">
        <Button
          type="button"
          size="lg"
          onClick={onHost}
          disabled={isPending || isEmpty}
          aria-label={hostAriaLabel}
          className="w-full gap-2 bg-gradient-to-bl from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 shadow-md hover:shadow-lg"
        >
          {isPending ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              <span>{preparingLabel}</span>
            </>
          ) : (
            <>
              <Play className="h-5 w-5 fill-current" aria-hidden="true" />
              <span>{hostLabel}</span>
            </>
          )}
        </Button>

        {isEmpty ? (
          <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-brand-500">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{needsQuestionsLabel}</span>
          </p>
        ) : null}
      </div>
    </article>
  )
}

function DashboardSkeleton() {
  return (
    <ul
      role="list"
      aria-hidden="true"
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="h-56 rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-100 ps-5 pe-5 py-5 animate-pulse"
        >
          <div className="h-5 w-3/4 rounded bg-brand-100" />
          <div className="mt-3 h-3.5 w-full rounded bg-brand-50" />
          <div className="mt-2 h-3.5 w-5/6 rounded bg-brand-50" />
          <div className="mt-5 h-6 w-20 rounded-full bg-brand-50" />
          <div className="mt-5 h-11 w-full rounded-md bg-brand-100" />
        </li>
      ))}
    </ul>
  )
}

type DashboardEmptyStateProps = {
  title: string
  description: string
  ctaLabel: string
}

function DashboardEmptyState({
  title,
  description,
  ctaLabel,
}: DashboardEmptyStateProps) {
  return (
    <div className="rounded-3xl border border-dashed border-brand-200 bg-white/60 px-6 py-16 text-center">
      <div
        className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
        aria-hidden="true"
      >
        <FilePlus2 className="h-10 w-10 text-brand-600" strokeWidth={1.5} />
      </div>
      <h2 className="text-2xl font-bold font-display text-brand-900 tracking-tight mb-2">
        {title}
      </h2>
      <p className="text-brand-700 mb-6 max-w-md mx-auto leading-relaxed">
        {description}
      </p>
      <a href="/quizzes/new" className="inline-block">
        <Button size="lg" className="gap-2">
          <Sparkles className="h-5 w-5" aria-hidden="true" />
          <span>{ctaLabel}</span>
        </Button>
      </a>
    </div>
  )
}

/**
 * Format a date as an Arabic relative time ("منذ ٣ أيام", "منذ ساعة") using
 * `Intl.RelativeTimeFormat`, falling back to an absolute `yyyy/mm/dd` date
 * (Western digits, no directional marks) for anything older than a year.
 * Mirrors the helper used by the `/quizzes`
 * list so the "last updated" affordance reads identically on both surfaces.
 */
function formatRelativeAr(date: Date): string {
  const diffMs = date.getTime() - Date.now()
  const diffMin = Math.round(diffMs / 60_000)
  const rtf = new Intl.RelativeTimeFormat('ar', { numeric: 'auto' })

  const absMin = Math.abs(diffMin)
  if (absMin < 1) return rtf.format(0, 'minute')
  if (absMin < 60) return rtf.format(diffMin, 'minute')

  const diffHour = Math.round(diffMs / 3_600_000)
  if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour')

  const diffDay = Math.round(diffMs / 86_400_000)
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day')

  const diffMonth = Math.round(diffDay / 30)
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, 'month')

  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}
